import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import FormFooter from "../../Shared/FormFooter";
import AlertRed from "../../Shared/AlertRed";
import AlertGreen from "../../Authentication/AlertGreen";
import BookingPageFormSkeleton from "../../Shared/LoadingSkeletons/BookingPageFormSkeleton";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { SunIcon, MoonIcon } from "@heroicons/react/24/outline";
import {
  useBookingPage,
  useUpdateBrandingTab,
} from "../../../hooks/useBookingPage";
import { getColorValue } from "../../../utility/colorUtils";
import {
  THEME_MODES,
  PRIMARY_COLORS,
  DEFAULT_COLORS,
} from "../../../config/colors";

const VisualDesignForm = () => {
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch config data
  const { data: configData, isLoading } = useBookingPage();

  // Update mutation for branding tab
  const { mutate: updateBranding, isPending: isUpdating } =
    useUpdateBrandingTab();

  const {
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      primaryColor: DEFAULT_COLORS.primary,
      themeMode: DEFAULT_COLORS.themeMode,
    },
  });

  // Watch form values
  const primaryColor = watch("primaryColor");
  const themeMode = watch("themeMode");

  const bookingPageSlug = configData?.data?.slug;

  // Set form values when data loads
  useEffect(() => {
    if (configData?.data) {
      const theme = {
        primaryColor: configData.data.primaryColor || DEFAULT_COLORS.primary,
        themeMode: configData.data.themeMode || DEFAULT_COLORS.themeMode,
      };

      // Reset form with new data
      reset(theme, { keepDirty: false });
    }
  }, [configData, reset]);

  const handleThemeModeChange = (mode) => {
    setValue("themeMode", mode, { shouldDirty: true });
  };

  const handlePrimaryColorChange = (color) => {
    setValue("primaryColor", color, { shouldDirty: true });
  };

  const onSubmit = async (data) => {
    setSuccessMessage("Visual design updated successfully");
    await performSave(data);
  };

  const performSave = async (data) => {
    setErrors({});
    setSuccess(false);

    updateBranding(data, {
      onSuccess: () => {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage("");
        }, 3000);
      },
      onError: (err) => {
        const errorMessage = err.response?.data?.error?.message;
        const errorDetails = err.response?.data?.error?.details;

        let displayMessage = errorMessage || "Failed to update visual design";

        // Handle specific validation errors
        if (errorDetails && Array.isArray(errorDetails)) {
          displayMessage = errorDetails.join(", ");
        }

        setErrors({
          title: "Error",
          message: displayMessage,
        });
      },
    });
  };

  if (isLoading) {
    return (
      <TabFormWrapper>
        <BookingPageFormSkeleton inputCount={2} />
      </TabFormWrapper>
    );
  }

  return (
    <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-8">
        {/* Theme Mode Selection */}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-2">
            Theme
          </label>
          <p className="text-xs text-ink-600 mb-4 h-4">
            {THEME_MODES.find((m) => m.value === themeMode)?.label || ""}
          </p>
          <div
            role="radiogroup"
            aria-label="Theme mode"
            className="inline-flex bg-cream-100 rounded-pill p-1"
          >
            {THEME_MODES.map((mode) => {
              const Icon = mode.value === "light" ? SunIcon : MoonIcon;
              const isSelected = themeMode === mode.value;
              return (
                <button
                  key={mode.value}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => handleThemeModeChange(mode.value)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-pill text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ink-400 ${
                    isSelected
                      ? "bg-ink-900 text-cream-50 shadow-hairline"
                      : "text-ink-700 hover:text-ink-900"
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {mode.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Primary Color Selection */}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-2">
            Primary Color
          </label>
          <p className="text-xs text-ink-600 mb-4 h-4">
            {PRIMARY_COLORS.find((c) => c.value === primaryColor)?.label || ""}
          </p>

          {/* Legacy-palette hint: shown when current value isn't in the
              refreshed PRIMARY_COLORS (e.g. existing customers on rose-600). */}
          {primaryColor &&
            !PRIMARY_COLORS.some((c) => c.value === primaryColor) && (
              <p className="text-xs text-ink-500 mb-3">
                We refreshed our color palette. Your current pick is still
                active — choose any new one to update.
              </p>
            )}

          <div className="flex flex-wrap gap-3">
            {PRIMARY_COLORS.map((color) => {
              const colorValue = getColorValue(color.value);
              const isSelected = primaryColor === color.value;

              return (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => handlePrimaryColorChange(color.value)}
                  aria-label={color.label}
                  aria-pressed={isSelected}
                  title={color.label}
                  className={`h-11 w-11 rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ink-400 ${
                    isSelected
                      ? "ring-2 ring-offset-2 ring-ink-900 scale-110"
                      : "hover:scale-110 ring-1 ring-ink-200"
                  }`}
                  style={{ backgroundColor: colorValue }}
                />
              );
            })}
          </div>
        </div>

        {/* Real preview — open the actual booking page in a new tab. */}
        {bookingPageSlug && (
          <p className="text-sm text-ink-500">
            Changes save instantly to your live booking page.{" "}
            <a
              href={`${window.location.origin}/book/${bookingPageSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium text-accent-600 hover:text-accent-700 underline-offset-2 hover:underline"
            >
              Open it in a new tab
              <ArrowTopRightOnSquareIcon aria-hidden="true" className="size-3.5" />
            </a>
          </p>
        )}
      </div>

      <FormFooter
        primaryCaption="Save"
        isLoading={isUpdating}
        disabled={!isDirty}
        requiresVerification={false}
      />

      {/* Alerts */}
      {errors.title && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertRed title={errors.title} message={errors.message} />
        </div>
      )}

      {success && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertGreen title="Success" message={successMessage} />
        </div>
      )}
    </TabFormWrapper>
  );
};

export default VisualDesignForm;
